<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>{{ $t("nav.createImmediateTagging") }}</h2>
      </template>
    </Toolbar>
    <form
      class="details"
      @submit.prevent="createImmediateTagging"
    >
      <StakeholderSelectionComponent
        v-model="stakeholder"
      />
      <UserSelectionComponent
        v-if="stakeholder"
        v-model="user"
        :stakeholder="stakeholder"
      />
      <div
        v-if="displayVenueSelection"
        class="p-input mb-6"
      >
        <h3 class="mb-2">
          <label
            for="mainVenue"
            class="required-input"
          >{{ $t('venueselection.title') }}</label>
        </h3>
        <Dropdown
          id="mainVenue"
          v-model="v$.mainVenue.$model"
          :options="venues"
          :option-label="(value) => $i18n.locale === 'fr' ? value.frenchWording : value.englishWording"
          :placeholder="$t('venueselection.select')"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.mainVenue.$error }"
          required
        />
        <div
          v-for="error of v$.mainVenue.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div
        v-if="user && mainVenue"
        class="details"
      >
        <h3 class="my-0">
          {{ $t('nav.immediateTaggingSummary') }}
        </h3>
        <div class="grid">
          <div class="col-12 lg:col-6">
            <h4 class="my-3">
              {{ $t('entity.user.user') }}
            </h4>
            <p class="mt-2">
              {{ $t('entity.user.name') }} : <strong>{{ user.firstname }} {{ user.lastname }}</strong>
            </p>
            <p class="mt-2">
              {{ $t('entity.user.company') }} : <strong>{{ user.company }}</strong>
            </p>
            <p v-if="user.email && user.email !== ''" class="mt-2">
              {{ $t('entity.user.email') }} : <strong>{{ user.email }}</strong>
            </p>
            <p
              v-if="user.phoneNumber"
              class="mt-2"
            >
              {{ $t('entity.user.phoneNumber') }} : <strong>{{ formatPhoneNumberAsInternational(user.phoneNumber, user.phoneNumberCountryCode) }}</strong>
            </p>
          </div>
          <div class="col-12 lg:col-6">
            <h4 class="my-3">
              {{ $t('entity.user.stakeholderId') }}
            </h4>
            <p class="mt-2">
              {{ $t('entity.stakeholder.stakeholderId') }} : <strong>{{ user.stakeholder.stakeholderId }}</strong>
            </p>
            <p class="mt-2">
              {{ $t('entity.stakeholder.name') }} : <strong>{{ user.stakeholder.name }}</strong>
            </p>
          </div>
        </div>
        <div>
          <h4 class="my-3">
            {{ $t('entity.lineId.mainVenue') }}
          </h4>
          <p class="mt-2">
            <strong>{{ $i18n.locale === 'fr' ? mainVenue.frenchWording : mainVenue.englishWording }}</strong>
          </p>
        </div>
        <div class="text-right">
          <Button
            type="submit"
            :label="$t('action.crud.create')"
            icon="pi pi-save"
            :disabled="createButtonDisabled"
            :loading="isSaving"
            class="p-button-rounded p-button-outlined"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Toolbar from "primevue/toolbar";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import {required} from "@/i18n/i18nValidators";
import {useVuelidate} from '@vuelidate/core';
import StakeholderSelectionComponent from "@/components/stakeholder/StakeholderSelectionComponent.vue";
import UserSelectionComponent from "@/components/user/UserSelectionComponent.vue";
import {reactive} from "vue";
import useVenueFilter from "@/composables/filters/useVenueFilter";
import {formatPhoneNumberAsInternational} from "@/shared/utils/phoneNumberUtils";

export default {
  name: 'CreateImmediateTaggingView',
  components: {UserSelectionComponent, StakeholderSelectionComponent, Button, Toolbar, Dropdown},
  setup() {
    const venueFilter = reactive(useVenueFilter());
    venueFilter.filters.active.value = true;

    return {
      venueFilter,
      v$: useVuelidate()
    }
  },
  data() {
    return {
      venues: [],
      stakeholder: null,
      user: null,
      mainVenue: null,
      isSaving: false,
      displayVenueSelection: false
    }
  },
  computed: {
    createButtonDisabled() {
      return this.v$.user.$invalid || this.v$.mainVenue.$invalid;
    },
  },
  watch: {
    stakeholder() {
      this.user = null;
    },
    user() {
      if (!this.displayVenueSelection) {
        this.displayVenueSelection = this.stakeholder && this.user;
      }
    }
  },
  validations() {
    return {
      user: {required},
      mainVenue: {required},
    }
  },
  async mounted() {
    this.venues = (await this.$venueService.fetchVenuesPagingAndSorting(this.venueFilter.getQueryParams())).content;
  },
  methods: {
    formatPhoneNumberAsInternational,
    async createImmediateTagging() {
      this.isSaving = true;
      try {
        const immediateTagging = await this.$immediateTaggingService.create({
          userId: this.user.id,
          mainVenueId: this.mainVenue.id
        });
        this.$router.push({name: 'immediateTaggingDetail', params: {id: immediateTagging.id}});
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ');
        this.$emitter.emit('toast', {severity: 'error', message: errors});
      } finally {
        this.isSaving = false;
      }
    }
  }
}
</script>

<style scoped lang="scss">
#time {
  text-transform: capitalize;
}

.details {
  border: 2px solid $light-grey;
  padding: 20px;
}
</style>

